import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import classNames from 'classnames';
import { TaskScopeNameEnum } from 'graphql-common';
import CountUp from '@lib/components/CountUp/CountUp';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Typography from '@lib/components/Typography/Typography';
import Trans from '@lib/components/Trans/Trans';
import IconSurface from '@lib/components/IconSurface/IconSurface';
import { Colors } from '@lib/enums/colors';
import CountMark, { CountMarkTypes } from '@lib/components/CountMark/CountMark';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import { TaskCounter } from '@lib/interfaces/task';
import styles from './TasksOverviewStateCounters.module.scss';

const getCounters = ({
  active = 0,
  dashboardAll = 0,
  deadlineMissedWithoutReason = 0,
  failureSolved = 0,
  failureUnsolved = 0,
  success = 0,
  warningSolved = 0,
  warningUnsolved = 0,
}: TaskCounter) => [
  {
    type: TaskScopeNameEnum.DashboardAll,
    title: 'completions',
    count: dashboardAll,
    icon: 'verified',
    color: Colors.Lime,
    symbolsOutlined: false,
  },
  {
    type: TaskScopeNameEnum.Active,
    title: 'active-tasks',
    count: active,
    icon: 'schedule',
    color: Colors.Blue,
    symbolsOutlined: false,
  },
  {
    type: TaskScopeNameEnum.DeadlineMissed,
    title: 'deadlines-missed',
    count: deadlineMissedWithoutReason,
    icon: 'clock_loader_20',
    color: Colors.Corral,
    symbolsOutlined: true,
  },
  {
    type: TaskScopeNameEnum.Success,
    title: 'success-total',
    count: success,
    icon: 'check_circle',
    color: Colors.Green,
    symbolsOutlined: false,
  },
  {
    type: TaskScopeNameEnum.Warning,
    title: 'unresolved-warning',
    count: warningUnsolved,
    subCount: warningSolved,
    icon: 'warning',
    color: Colors.Orange,
    symbolsOutlined: false,
  },
  {
    type: TaskScopeNameEnum.Failure,
    title: 'unresolved-alert',
    count: failureUnsolved,
    subCount: failureSolved,
    icon: 'error',
    color: Colors.Red,
    symbolsOutlined: false,
  },
];

type TaskResultStateCountersProps = {
  taskCounterData: TaskCounter;
  tasksFirstLoading: boolean;
  tasksError?: ErrorLike;
};

export default function TasksOverviewStateCounters(
  props: TaskResultStateCountersProps,
) {
  const { taskCounterData, tasksFirstLoading, tasksError } = props;
  const { t } = useTranslation();
  if (tasksError) return null;
  if (tasksFirstLoading) return null;
  const counters = getCounters(taskCounterData);
  return (
    <PageBody>
      <div className={styles.counters}>
        {counters.map(
          ({ type, title, count, icon, color, subCount, symbolsOutlined }) => (
            <div
              key={type}
              data-test-type={type}
              className={styles.counterItem}
            >
              <IconSurface
                icon={icon}
                color={color}
                size="lg"
                symbolsOutlined={symbolsOutlined}
                surfaceClassName={styles.counterItemIconCircle}
                iconClassName={styles.counterItemIcon}
              />
              <div>
                <Typography variant="caption" strong className={styles.title}>
                  <Trans i18nKey={title} />
                </Typography>
                <div className={styles.countersRow}>
                  <Typography variant="h2" className={styles.counter}>
                    <CountUp end={count} duration={3} />
                  </Typography>
                  {!!subCount && (
                    <Tooltip body={t('solved')}>
                      <div className={styles.solvedCounter}>
                        <CountMark
                          value={Math.round(subCount)}
                          type={CountMarkTypes.blue}
                          disabled
                          useCountUp
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          ),
        )}
      </div>
    </PageBody>
  );
}
